import { useEffect, useMemo, useState } from "react";
import {
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  MessageCircle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";


type NavLinkItem = {
  label: string;
  hash: string;
};

type VideoSlide = {
  id: string;
  title: string;
  previewType: "video" | "image" | "solid";
  videoUrl?: string;
  posterSrc?: string;
  previewSrc?: string;
  previewColor?: string;
  thumbnailType: "image" | "solid";
  thumbnailSrc?: string;
  thumbnailColor?: string;
};

type VideoMedia =
  | { kind: "youtube"; src: string }
  | { kind: "iframe"; src: string }
  | { kind: "native"; src: string };

type CourseCard = {
  title: string;
  duration: string;
  description: string;
  slug?: string;
};

type MasterclassCard = {
  title: string;
  body: string;
  cta: string;
  slug?: string;
};

type StudioService = {
  title: string;
  body: string[];
  slug: string;
};

type SubPageSection = {
  heading: string;
  body?: string[];
  bullets?: string[];
};

type SubPageContent = {
  title: string;
  heroSummary: string;
  duration: string;
  sections: SubPageSection[];
};

const navLinks: NavLinkItem[] = [
  { label: "Courses", hash: "#courses" },
  { label: "Studio & Sevices", hash: "#studio" },
  { label: "Works", hash: "#portfolio" },
  { label: "Team", hash: "#team" },
];
  
const heroHighlights = [
  { label: "Our Studio & services", href: "#studio" },
  { label: "What we’ve done", href: "#portfolio" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Team", href: "#team" },
];


const VIDEO_FILE_EXTENSIONS = new Set(["mp4", "webm", "ogg"]);
const YOUTUBE_ID_REGEX =
  /(?:youtu\.be\/|youtube\.com\/(?:shorts\/|embed\/|watch\?v=|v\/))([A-Za-z0-9_-]{11})/i;

function resolveVideoMedia(slide: VideoSlide): VideoMedia | null {
  if (slide.previewType !== "video" || !slide.videoUrl) {
    return null;
  }

  const trimmedUrl = slide.videoUrl.trim();
  const youtubeMatch = trimmedUrl.match(YOUTUBE_ID_REGEX);

  if (youtubeMatch) {
    const videoId = youtubeMatch[1];
    return {
      kind: "youtube",
      src: `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`,
    };
  }

  const extension = trimmedUrl.split("?")[0].split(".").pop()?.toLowerCase();
  if (extension && VIDEO_FILE_EXTENSIONS.has(extension)) {
    return { kind: "native", src: trimmedUrl };
  }

  return { kind: "iframe", src: trimmedUrl };
}

const videoSlides: VideoSlide[] = [
  {
    id: "video-1",
    title: "Team Advaitha Showcase",
    previewType: "video",
    videoUrl: "https://youtu.be/jYeJzFp1Z8w?si=K4_ara3qEBOBhOwW",
    posterSrc: "/assets/figma/video-thumb-1.png",
    thumbnailType: "image",
    thumbnailSrc: "/assets/figma/video-thumb-1.png",
  },
  {
    id: "video-2",
    title: "Studio Spotlight",
    previewType: "image",
    previewSrc: "/assets/figma/1_Home Slider_2.jpg",
    thumbnailType: "image",
    thumbnailSrc: "/assets/figma/video-thumb-1.png",
  },
  {
    id: "video-3",
    title: "Student Projects",
    previewType: "image",
    previewSrc: "/assets/figma/1_Home Slider_3.jpg",
    thumbnailType: "image",
    thumbnailSrc: "/assets/figma/video-thumb-2.png",
  },
  {
    id: "video-4",
    title: "Behind the Scenes",
    previewType: "video",
    videoUrl: "https://youtu.be/TWLEi50UBN0",
    posterSrc: "/assets/figma/video-thumb-3.png",
    thumbnailType: "image",
    thumbnailSrc: "/assets/figma/video-thumb-3.png",
  },
];

const courses: CourseCard[] = [
  {
    title: "Acting Courses",
    slug: "acting-courses",
    duration: "3 Months & 6 Months",
    description:
      "This Course is designed for students and working professionals who are passionate about acting but need a flexible, practical learning experience.",
  },
  {
    title: "Acting Courses for Kids",
    slug: "acting-courses-for-kids",
    duration: "6 Months & 1 Year",
    description:
      "This course is designed to introduce young talent to the world of acting playfully and engagingly.",
  },
  {
    title: "Direction Courses",
    slug: "direction-courses",
    duration: "3 Months & 6 Months",
    description:
      "This course is tailored for aspiring directors, writers, and visual storytellers who seek practical, hands-on training rooted in industry realities.",
  },
];

const masterclasses: MasterclassCard[] = [
  {
    title: "Music Classes",
    slug: "music-classes",
    body: "We cover basics to intermediate classes on Keyboard, Guitar, Hindustani & Carnatic Vocal.",
    cta: "Details",
  },
  {
    title: "Filmmaking Masterclass",
    slug: "filmaking-masterclass",
    body: "We invite industry professionals for this Monthly Masterclass which covers all the technical & practical processes from Story to Screen and everything in between.",
    cta: "Details",
  },
];

const studioServices: StudioService[] = [
  {
    title: "Camera & Light Rentals",
    slug: "camera-and-light-rentals",
    body: [
      "ARRI Alexa LF with Lenses",
      "SONY FX3 (NETFLIX Ready)",
      "Gimbal & Lights",
    ],
  },
  {
    title: "Dubbing & Song Recording",
    slug: "dubbing-and-song-recording",
    body: [
      "Sound Engineer with 4 years experience",
      "On-spot pitch correction Song Recording",
      "Emotional Correction for Dubbing",
    ],
  },
  {
    title: "Editing & Post-Production",
    slug: "editing-and-post-production",
    body: [
      "SFX-Mixing-Mastering",
      "Color Grading (Di)",
      "VFX, 3D & Previz",
    ],
  },
  {
    title: "Music Production",
    slug: "music-production",
    body: ["4 Years Experience", "Song Composition", "BGMs"],
  },
  {
    title: "Audio Company",
    slug: "audio-company",
    body: [
      "Music Video Production",
      "Song Distribution",
      "Song Purchase",
    ],
  },
  {
    title: "Ad Shoot & Photoshoot",
    slug: "adshoot-and-photoshoot",
    body: [
      "Professional Ad films",
      "Product Shoot",
      "Portfolio Shoot",
    ],
  },
];

const portfolioItems = [
  {
    title: "1st Viral Short Film",
    videoUrl: "https://www.youtube.com/embed/O3iEfC_SONU?si=1CuWs7OXMNgaZSHR",
    thumbnail: "/images/portfolio/video1.jpg", // Optional
  },
  {
    title: "Youtube Stars",
    videoUrl: "https://www.youtube.com/embed/zPa_TYSHjzg?si=NX20AO2oVXsWNC7p",
    thumbnail: "/images/portfolio/video2.jpg",
  },
  {
    title: "Sci Fi",
    videoUrl: "https://www.youtube.com/embed/TWLEi50UBN0?si=eo2MBabcNL3TmTCz",
    thumbnail: "/images/portfolio/video3.jpg",
  },
  {
    title: "Social Message",
    videoUrl: "https://www.youtube.com/embed/afXUN8mZiSw?si=e_7tIGsuJzOjVuum",
    thumbnail: "/images/portfolio/video4.jpg",
  },
  {
    title: "Kids Comedy",
    videoUrl: "https://www.youtube.com/embed/OORqQvNj8JA?si=qQmczaSfSWrVVTsC",
    thumbnail: "/images/portfolio/video5.jpg",
  },
    {
    title: "Commercial Film",
    videoUrl: "https://www.youtube.com/embed/LteBUZFxCoU?si=KNC0eAi07NovfkRB",
    thumbnail: "/images/portfolio/video5.jpg",
  },
];


const testimonials = [
  {
    name: "Samuel Sam",
    role: "Actor",
    text: "This is a fantastic space for aspiring theater or film artists. What sets it apart is Gangadhar sir—more than a teacher, he’s a true mentor who supports your personal and artistic growth. The filmmaking course gave me real, hands-on experience. Grateful to be part of Team Advaitha!",
  },
  {
    name: "Adarsh Bellur",
    role: "Store Owner",
    text: "This top film direction institute provides an exceptional learning experience through hands-on involvement in live shooting. The institute's focus on practical training creates an immersive environment that surpasses traditional classroom settings. Learning through actual shooting scenarios gives students insights and skills that theory alone cannot offer.",
  },
  {
    name: "Sona Sara",
    role: "IT Professional",
    text: "This top film direction institute provides an exceptional learning experience through hands-on involvement in live shooting. The institute's focus on practical training creates an immersive environment that surpasses traditional classroom settings. Learning through actual shooting scenarios gives students insights and skills that theory alone cannot offer.",
  },
];

const teamMembers = [
  { name: "Gangadhar A Nagathihalli", role: "Founder/Director",photo:"/assets/figma/4_Team_1.jpg", profileLink:"https://www.instagram.com/gangadhara_nagathihalli/" },
  { name: "Ashok", role: "Co-founder & Director", profileLink:"https://www.instagram.com/ashokgowdru94/", photo:"/assets/figma/4_Team_2.jpg"},
  { name: "Sunny FX", role: "Creative Lead of Post-Production & Strategy", profileLink:"https://www.behance.net/sunilfx", photo:"/assets/figma/4_Team_3.jpg"},
  { name: "Harshith B Gowda", role: "Cinematographer", profileLink:"https://www.instagram.com/harshith_b_gowda/", photo:"/assets/figma/4_Team_4.jpg"},
  { name: "Vidhunya Gowda", role: "Actress & Admin", profileLink:"https://www.instagram.com/vidhunya_gowda/", photo:"/assets/figma/4_Team_5.jpg"},
  { name: "Chetan S Ram", role: "Actor & Instructor", profileLink:"https://www.instagram.com/chethan_s_ram/", photo:"/assets/figma/4_Team_9.jpg"},
  { name: "Pralaksh Actor", role: "Actor & Jr Editor", profileLink:"https://www.instagram.com/__pralaksh__/", photo:"/assets/figma/4_Team_10.jpg"},
  { name: "R Maruti", role: "Music Director", profileLink:"https://www.instagram.com/_music_director_maruti_/", photo:"/assets/figma/4_Team_6.jpg"},
  { name: "Shivanand", role: "Dramatics Division Coordinator", profileLink:"https://www.instagram.com/shivanand.hegde.7/", photo:"/assets/figma/4_Team_7.jpg"},
  { name: "Deekshith", role: "Photography & Production Manager", profileLink:"https://www.instagram.com/deekshithb_/", photo:"/assets/figma/4_Team_8.jpg"},
];

const alumni = [
  {
    img: "/assets/figma/2_Alumni_5.jpg",
    link: "https://www.instagram.com/soumyaamarapurkar/",
  },
  {
    img: "/assets/figma/2_Alumni_2.jpg",
    link: "https://www.instagram.com/shree_bhavya_official/",
  },
  {
    img: "/assets/figma/2_Alumni_3.jpg",
    link: "https://www.instagram.com/spoorthi___ramitha/",
  },
  {
    img: "/assets/figma/2_Alumni_4.jpg",
    link: "https://www.instagram.com/adithyakaregowda/",
  },
  {
    img: "/assets/figma/2_Alumni_1.jpg",
    link: "https://www.instagram.com/gauravshettyy3/",
  },
];


const actingCourseLessons = [
  {
    title: "Lesson 1",
    detail: "Fundamentals of Acting",
  },
  {
    title: "Lesson 2",
    detail: "Theatre Style Acting",
  },
  {
    title: "Lesson 3",
    detail: "Camera Acting",
  },
  {
    title: "Lesson 4",
    detail: "Stage Plays (Theatre Performance)",
  },
  {
    title: "Lesson 5",
    detail: "Acting in Short Films",
  },
  {
    title: "Lesson 6",
    detail: "Dubbing Practices on Live Projects",
  },
  {
    title: "Lesson 7",
    detail: "Audition Preparation",
  },
  {
    title: "Lesson 8",
    detail: "Photo-shoots and Portfolio Creation",
  },
  {
    title: "Lesson 9",
    detail: "Personal & Character Development",
  },
  {
    title: "Lesson 10",
    detail: "Industry Exposure (Audition Listings, References in Serials and Sandalwood Films)",
  },
  {
    title: "Lesson 11",
    detail: "Certification on Completion",
  },
  {
    title: "Lesson 12",
    detail: "Career Preparation & Opportunities",
  },
];


const directionCourseLessons = [
  {
    title: "Lesson 1 - Scriptwriting",
    detail:
      "Learn the art of storytelling from scratch — explore sources of story ideas, script formatting, narrative structure, screenwriting techniques, storyboarding, screenplay development, and writing impactful dialogues.",
  },
  {
    title: "Lesson 2 - Cinematography",
    detail:
      "Get hands-on experience with camera operations. Master the basics using professional equipment like Sony FX3, Arri, & drone cameras. Understand lighting techniques, frame composition, and visual storytelling.",
  },
  {
    title: "Lesson 3 - Editing",
    detail:
      "Understand the fundamentals of film editing and gain practical skills using industry-standard tools such as DaVinci Resolve & Adobe Premiere Pro.",
  },
  {
    title: "Lesson 4 - Pre-Production",
    detail:
      "Learn how to effectively plan a film project. Topics include scheduling, budgeting, equipment hiring, and understanding film union rules and regulations.",
  },
  {
    title: "Lesson 5 - Production",
    detail:
      "Experience the process of shooting a film, including art direction, makeup, costume design, continuity management, and maintaining production reports.",
  },
  {
    title: "Lesson 6 - Music Direction",
    detail:
      "Dive into the basics of music theory, composition, and songwriting, tailored specifically for film scoring and background music.",
  },
  {
    title: "Lesson 7 - Post-Production",
    detail:
      "Explore the complete post-production workflow — editing, dubbing, adding visual effects, creating title cards, sound design, background scores, and color grading.",
  },
  {
    title: "Lesson 8 - Film Distribution & Marketing",
    detail:
      "Understand how to take your film to the world — covering film rights, censorship, subsidies, award circuits, distribution (local & international), and marketing strategies across TV, digital platforms, and more.",
  },
  {
    title: "Lesson 9 - Short Film Making",
    detail:
      "Work hands-on to develop and direct your own short film. Learn about documentary structures, filmmaking objectives, and the practical aspects of bringing your story to the screen.",
  },
];

const directionCourseOutcomes = [
  "Career preparation & opportunities",
  "By the end of the course, students are:",
  "Confident in leading a film crew and managing on-set dynamics.",
  "Skilled in visual storytelling, script breakdown, and shot planning.",
  "Capable of handling real-world production challenges.",
  "Equipped with a directed short film for their portfolio.",
  "Ready to assist or work independently in short films, ads, web series, and feature films.",
  "Continuously supported with networking, production updates, and freelance opportunities.",
];

const directionCourseFaculty = [
  "Experienced Film Directors & Assistant Directors",
  "Renowned Cinematographers, Scriptwriters & Editors",
  "Guest Industry Professionals sharing current insights and techniques",
];

const musicClassSyllabus = [
  {
    title: "Beginner",
    bullets: [
      "Introduction to Instrument",
      "Music theory & harmony",
      "Timing & rhythm",
      "Popular standards & melodies",
      "Chords & progressions",
      "Finger independence",
      "Scales & modes",
      "Introduction to recording & composing",
      "Practice material & backing tracks",
    ],
  },
  {
    title: "Advanced",
    bullets: [
      "Music theory",
      "Harmony & chord extensions/voicings",
      "Time signatures & finger exercises",
      "Introduction to Blues/RnB/Gospel",
      "Introduction to jazz",
      "Standards & melodies",
      "Recording & composition",
      "Altered harmony & soloing",
    ],
  },
];

const masterclassTheory = [
  "Intro - What to expect",
  "Case Studies",
  "10,000 Hours - Difference between Mediocrity and Genius",
  "Idea - Tour of Idea Factory",
  "Story-Screenplay",
  "Acting - Emote & Breathe",
  "Basics - Base of everything",
  "Process - Storyboard-Production-Promotion",
  "Leadership - Sustain to succeed",
  "Talent - Exposure and Restlessness",
  "Artist - Born to Express",
  "Attitude - Question everything",
  "Google Suite - Most Important Tool",
  "Asset Management - Keep your Desktop clean",
  "Followers - Not likers, we need Patrons",
  "Digital Marketing - Know what not to do!",
  "What not to get Inspired: Big Fish in a small Pond - Follow the small",
];

const masterclassPracticals = [
  "Inspiration - Brainstorming sessions",
  "Writing - 1st Screenplay",
  "Planning - Project Decks",
  "Previz - Shoot for Edit (With Music)",
  "Shoot - Lights-Action-cut",
  "Dub - Voice Acting",
  "Edit - 2nd Screenplay",
  "Color Grading - Colors & Emotions",
  "Sound - SFX & Mixing",
  "Publish - Poster-Thumbnail-SEO",
];

const gearCategories = [
  {
    title: "Sony FX3",
    subtitle: "Compact Full-Frame Cinema",
    image: "https://public.youware.com/image/ba734925-a692-448a-bcd0-01ce469f24e2/hq97wnyidy.png",
  },
  {
    title: "35 MM",
    subtitle: "Classic Everyday Lens",
    image: "/assets/gear/gear-09.png",
  },
  {
    title: "85 MM",
    subtitle: "Portrait Prime Perfection",
    image: "/assets/gear/gear-08.png",
  },
  {
    title: "NDPL Filter",
    subtitle: "Control Light, Cinematic Shots",
    image: "/assets/gear/gear-03.png",
  },
  {
    title: "Ninja Monitor",
    subtitle: "Sharp Monitoring, Easy Setup",
    image: "/assets/gear/gear-02.png",
  },
  {
    title: "Gimbal",
    subtitle: "Smooth, Stable Motion",
    image: "/assets/gear/gear-11.png",
  },
  {
    title: "LC 500",
    subtitle: "Portable Lighting Solution",
    image: "/assets/gear/gear-06.png",
  },
  {
    title: "Godox 100 bi",
    subtitle: "Powerful Bi-Color Light",
    image: "/assets/gear/gear-01.png",
  },
  {
    title: "Matt Box",
    subtitle: "Glare-Free Cinematic Control",
    image: "/assets/gear/gear-13.png",
  },
  {
    title: "Reflector",
    subtitle: "Bounce Light, Add Glow",
    image: "/assets/gear/gear-05.png",
  },
  {
    title: "Tripod",
    subtitle: "Stable Support, Easy Adjust",
    image: "/assets/gear/gear-14.png",
  },
  {
    title: "ALEXA SXT W",
    subtitle: "High-End Cinema Standard",
    image: "/assets/gear/gear-04.png",
  },
  {
    title: "Ultra Prime Lenses",
    subtitle: "Sharp Optics, Cinematic Precision",
    image: "/assets/gear/gear-10.png",
  },
  {
    title: "Alura Zoom Lens (45–250)",
    subtitle: "Versatile Zoom, Pro Clarity",
    image: "/assets/gear/gear-07.png",
  },
];

const audioCompanyServices = [
  "Services",
  "Platforms we Distribute to",
  "Our Published Catalog",
  "Artists & Musicians we have collaborated with so far",
];

const contactOptions = [
  {
    label: "Email Us",
   href: "mailto:teamadvaithastudio@gmail.com",
    icon: Mail,
  },
  {
    label: "Call Us",
    href: "tel:+919035973590",
    icon: Phone,
  },
 {
  label: "Whatsapp",
  href: "https://wa.me/919035973590?text=Hey%20Team%20Advaitha!%20I%20found%20you%20through%20your%20website%20and%20wanted%20to%20connect.",
  icon: MessageCircle,
}
];

const branches = [
  {
    name: "Vijayanagar Branch",
    address: "1st Floor, 5th Cross, Near\nAttiguppe Metro, Subanna Garden\nMain Rd, Vijayanagar, Bengaluru-40",
    mapLink: "https://maps.app.goo.gl/VM8TrFtQSfdaf6kU9",
  },
  {
    name: "Indiranagar Branch",
    address: "874/B, 4th main road, 5th Cross Rd,\nHAL 3rd Stage, New Tippasandra, \nBengaluru, Karnataka 560075",
    mapLink: "https://maps.app.goo.gl/aeqB9miwu67vnN3n9",
  },
  {
    name: "Hebbal Branch",
    address: "Restless Feet Dance Studio, 2nd Floor,\n S V Arcade, G Block, 60 Feet Main Road Sahakara Nagar,\n Park View Layout, Sahakar Nagar, Bengaluru, Karnataka 560092",
    mapLink: "https://maps.app.goo.gl/3ZDZ5WLoBaVrnePF9",
  },
  {
    name: "Studio Location",
    address: "426, opp. Ambedkar college,\n Balagangadhara Nagar, Kengunte,\nMallathahalli, Bengaluru, Karnataka 560056",
    mapLink: "https://maps.app.goo.gl/AAkrWP9skyqDkyNE7",
  },
];

const kidCourseLessons = [
  {
    title: "Module 1",
    detail: "Confidence-building theatre games",
  },
  {
    title: "Module 2",
    detail: "Voice, diction & expression drills",
  },
  {
    title: "Module 3",
    detail: "Storytelling with props & movement",
  },
  {
    title: "Module 4",
    detail: "Camera-friendly improvisations",
  },
];

const kidCoursePricing = [
  {
    id: "kids-6-months",
    duration: "6 Months",
    price: "₹14,900",
    features: [
      "Theatre Style Acting",
      "1 Short Movie",
      "Photoshoot + Portfolio",
      "Characters in Shortfilms (Casting based on dedication & performance)",
      "Personality & Speech Development",
      "Maximum number of audition calls (During & after the course)",
      "Referral for TV Serials & Movies (Kannada)",
      "Certification",
    ],
  },
  {
    id: "kids-1-year",
    duration: "1 Year",
    price: "₹24,900",
    features: [
      "Theatre Style Acting",
      "2 Short Movies",
      "Photoshoot + Portfolio",
      "Theatre Shows",
      "Characters in Shortfilms (Casting based on dedication & performance)",
      "Personality & Speech Development",
      "Maximum number of audition calls (During & after the course)",
      "Referral for TV Serials & Movies (Kannada)",
      "Certification",
    ],
  },
];

const kidCourseLocations = [
  {
    label: "Batch Timings",
    description: "Only on Sundays · 11:00 AM – 2:00 PM",
  },
  {
    label: "Age Group",
    description: "4 to 12 years",
  },
  {
    label: "Campuses",
    description: "Bangalore- Vijayanagar, Indiranagar & Hebbal",
  },
];

const slugRoutes: Record<string, SubPageContent> = {
  "acting-courses": {
    title: "Acting Courses",
    heroSummary:
      "This Course is designed for students and working professionals who are passionate about acting but need a flexible, practical learning experience.",
    duration: "3 Months & 6 Months",
    sections: [
      {
        heading: "Course Overview",
        body: [
          "Learn through live projects, camera drills, and theatre workshops designed to build confidence in front of an audience and on set.",
          "Industry mentors and working actors share practical frameworks that shorten the learning curve.",
        ],
      },
      {
        heading: "What You Gain",
        bullets: [
          "Acting fundamentals that cover stage, camera, and dubbing techniques.",
          "Portfolio guidance including headshots, audition tapes, and networking support.",
          "Certification plus continuous audition updates even after course completion.",
        ],
      },
      {
        heading: "Syllabus Highlights",
        bullets: actingCourseLessons.map((lesson) => lesson.detail),
      },
    ],
  },
  "acting-courses-for-kids": {
    title: "Acting Courses for Kids",
    heroSummary:
      "This course introduces young talent to the world of acting through fun improvisation games, voice modulation drills, and camera-friendly exercises.",
    duration: "6 Months & 1 Year",
    sections: [
      {
        heading: "What They Learn",
        bullets: [
          "Confidence-building theatre exercises with weekly showcases.",
          "Storytelling and expression sessions guided by child-friendly mentors.",
          "Camera-facing etiquette, diction improvement, and foundational dance/movement.",
        ],
      },
      {
        heading: "Parent Takeaways",
        bullets: [
          "Track progress with monthly performance reports and recorded clips.",
          "Workshops on managing auditions, grooming etiquette, and stage readiness.",
        ],
      },
    ],
  },
  "direction-courses": {
    title: "Direction Courses",
    heroSummary:
      "A hands-on program built for aspiring directors who want end-to-end exposure from script to screen, powered by real-world assignments.",
    duration: "3 Months & 6 Months",
    sections: [
      {
        heading: "Program Structure",
        bullets: directionCourseLessons.map((lesson) => `${lesson.title}: ${lesson.detail}`),
      },
      {
        heading: "Outcomes",
        bullets: directionCourseOutcomes.slice(1),
      },
      {
        heading: "Mentorship",
        bullets: directionCourseFaculty,
      },
    ],
  },
  "music-classes": {
    title: "Music Classes",
    heroSummary:
      "Keyboard, Guitar, Hindustani & Carnatic Vocal programs tailored to help students play confidently and compose their own music.",
    duration: "Flexible Batches",
    sections: [
      {
        heading: "Beginner Track",
        bullets: musicClassSyllabus[0].bullets,
      },
      {
        heading: "Advanced Track",
        bullets: musicClassSyllabus[1].bullets,
      },
      {
        heading: "Why Choose Us",
        bullets: [
          "Mentorship from working musicians and session artists.",
          "Recording exposure inside our in-house audio suites.",
          "Personalized practice plans and performance reviews.",
        ],
      },
    ],
  },
  "filmaking-masterclass": {
    title: "Filmaking Masterclass",
    heroSummary:
      "An immersion for writers, directors, and technicians who want to master the 10,000-hour journey with intensive theory and practical drills.",
    duration: "7 Intensive Days",
    sections: [
      {
        heading: "Theory Modules",
        bullets: masterclassTheory,
      },
      {
        heading: "Practical Sessions",
        bullets: masterclassPracticals,
      },
      {
        heading: "Who Should Attend",
        bullets: [
          "Direction students completing the flagship program.",
          "Working creatives who want structured guidance and industry exposure.",
        ],
      },
    ],
  },
  "camera-and-light-rentals": {
    title: "Camera & Light Rentals",
    heroSummary:
      "Rent cinema-grade equipment and lighting packages curated for short films, commercials, and feature projects.",
    duration: "Request Custom Quote",
    sections: [
      {
        heading: "Popular Gear",
        bullets: gearCategories.map((gear) => `${gear.title} — ${gear.subtitle}`),
      },
      {
        heading: "Add-on Services",
        bullets: [
          "On-site technician support",
          "Delivery and pickup across Bengaluru",
          "Bundled pricing for multi-day productions",
        ],
      },
    ],
  },
  "dubbing-and-song-recording": {
    title: "Dubbing & Song Recording",
    heroSummary:
      "Professional Song Recording & Dubbing Services at affordable prices with a Sound Engineer with 4 years of experience.",
    duration: "Hourly / Project Packages",
    sections: [
      {
        heading: "Highlights",
        bullets: [
          "On-spot pitch correction for Song Recording.",
          "Emotional Correction for Dubbing.",
          "Affordable Prices for Independent Filmmakers.",
        ],
      },
      {
        heading: "Tech Specs",
        bullets: [
          "Audio-Technica AT2050 Multi-Pattern Condenser",
          "Microphone - Yamaha HS8 Studio Monitor",
          "Focusrite Scarlett 2i2 4th Gen audio interface",
          "Sennheiser Professional Audio HD 280 PRO",
          "Acoustic Soundproof Vocal Booth",
        ],
      },
    ],
  },
  "editing-and-post-production": {
    title: "Editing & Post Production",
    heroSummary:
      "One Stop Solution for Ad Films, Short Films, Webseries & Movies",
    duration: "Project Based",
    sections: [
      {
        heading: "Capabilities",
        bullets: [
          "DaVinci Resolve, Premiere Pro, After Effects workflows",
          "Color grading suites with calibrated monitors",
          "SFX, VFX, and 3D previz teams on call",
        ],
      },
      {
        heading: "Deliverables",
        bullets: [
          "Feature films, web series, TVCs, music videos",
          "Distribution-ready exports in all major codecs",
          "Photoshoots,Product Shoot, Corporate Events, Pre-wedding Shoots",
        ],
      },
    ],
  },
  "music-production": {
    title: "Music Production",
    heroSummary:
      "Compose, arrange, and produce original music with our in-house producers and session musicians.",
    duration: "Song / Campaign Packages",
    sections: [
      {
        heading: "What We Handle",
        bullets: [
          "Songwriting and arrangement",
          "Live and electronic instrumentation",
          "Mixing, mastering, and distribution support",
        ],
      },
      {
        heading: "Recent Collaborations",
        bullets: [
          "Indie artists across languages",
          "Brand anthems and ad films",
        ],
      },
    ],
  },
  "audio-company": {
    title: "Audio Company",
    heroSummary:
      "We distribute music to major streaming platforms and manage  copyrights, produce, & Distribute content for Independent artists and  Music Albums globally.",
    duration: "Annual Partnerships",
    sections: [
      {
        heading: "Highlights",
        bullets: [
          "We are an independent digital music distribution company.",
          "We endeavor to help our local and regional artists and generate revenue for them.",
          "We produce music Videos.",
          "We have over 20+ stores and distribute songs to all the major players in the music industry.",
        ],
      },
      {
        heading: "Distribution Platforms",
        bullets: [
          "Spotify",
          "Apple Music",
          "Amazon Music",
          "YouTube Music",
          "Wynk",
          "JioSaavn",
          "Gaana",
          "Deezer",
          "SoundCloud",
        ],
      },
    ],
  },
  "adshoot-and-photoshoot": {
  title: "Adflims",
  heroSummary:
    "Concept-to-camera production unit for brand ads, product shoots, and talent portfolios.",
  duration: "Rate Card",
  sections: [
    {
      heading: "Offerings",
      bullets: [
        "Creative direction and scripting",
        "Studio and outdoor production teams",
        "Retouching, edits, and final delivery",
      ],
    },
    {
      heading: "Ideal Clients",
      bullets: [
        "Lifestyle, FMCG, and startup brands",
        "Emerging artists and influencers",
      ],
    },
  ],
}

};





type DetailSlug = keyof typeof slugRoutes;

const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0 },
};

function ScrollToHash() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const target = document.querySelector(location.hash);
      if (target) {
        target.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname, location.hash]);

  return null;
}

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  const linkProps = (hash: string) => ({ pathname: "/", hash });

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/20 bg-brand-background/70 backdrop-blur-xl"
      style={{
        backgroundColor: "#000000b3"
      }}>
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 lg:px-8">
        <Link to="/" className="flex items-center gap-3" aria-label="Team Advaitha home">
          <img
            src="https://public.youware.com/image/1a706876-4791-4e60-958c-2977c76a93b4/4uzeutdgrz.png"
            alt="Team Advaitha logo"
            className="h-10 w-auto max-w-[160px] object-contain sm:h-12"
            style={{
              fontSize: "16px",
              fontWeight: "400",
              color: "rgb(16, 18, 23)",
              textAlign: "start",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderRadius: "0px",
              margin: "0px",
              padding: "20px 0px",
              marginTop: "0px",
              marginRight: "0px",
              marginBottom: "0px",
              marginLeft: "0px",
              paddingTop: "0px",
              paddingRight: "0px",
              paddingBottom: "0px",
              paddingLeft: "0px"
            }} />
        </Link>

        <nav className="hidden items-center gap-8 text-sm font-medium text-brand-muted lg:flex">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={linkProps(link.hash)}
              className="transition-colors hover:text-brand-ink"
              style={{
                color: "#d1d2d2"
              }}>
              {link.label}
            </Link>
          ))}
          <Link
            to={linkProps("#contact")}
            className="rounded-full bg-accent px-5 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-accent/90"
          >
            Contact
          </Link>
        </nav>

        <button
          type="button"
          className="lg:hidden"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Toggle navigation"
        >
          {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            className="border-t border-white/10 bg-brand-background px-6 pb-6 lg:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <ul className="space-y-4 pt-4 text-base font-medium text-brand-ink">
              {navLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={linkProps(link.hash)}
                    className="block rounded-xl bg-white/60 px-4 py-3 shadow-card transition hover:bg-white"
                    onClick={() => setMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={linkProps("#contact")}
                  className="flex items-center justify-center gap-2 rounded-full bg-accent px-5 py-3 text-white shadow-glow"
                  onClick={() => setMenuOpen(false)}
                >
                  Contact
                </Link>
              </li>
            </ul>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Layout() {
  return (
    <div className="bg-brand-background text-brand-ink">
      <SiteHeader />
      <ScrollToHash />
      <main>
        <Outlet />
      </main>

      <footer className="bg-surface py-10 text-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 text-sm lg:flex-row lg:items-center lg:justify-between lg:px-8">

          {/* Footer text */}
          <div>
            <p className="font-semibold uppercase tracking-[0.4em] text-white/70">Team Advaitha</p>
            <p className="mt-2 text-white/60">All rights reserved TEAM ADVAITHA 2025</p>
          </div>

          {/* Social media icons */}
          <div className="flex gap-4">
            <a
              href="https://www.youtube.com/@teamadvaitha"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
            >
              <img src="/assets/figma/youtube.png" alt="YouTube" width={24} height={24} />  
            </a>

            <a
              href="https://www.instagram.com/teamadvaitha?igsh=aTAyZGswcmpxazAx"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
            >
             <img src="/assets/figma/insta.png" alt="Instagram" width={24} height={24} />  
            </a>

            <a
              href="https://www.facebook.com/GANGADHARNAGATHIHALLI/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
            >
              <img src="/assets/figma/facebook.png" alt="facebook" width={24} height={24} />  
            </a>

            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
            >
              <img src="/assets/figma/twitter.png" alt="Twitter" width={24} height={24} />  
            </a>

            <a
              href="https://www.behance.net/sunilfx"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
            >
              <img src="/assets/figma/behance.png" alt="Behance" width={24} height={24} />  
            </a>
          </div>

          {/* Reach out button */}
          <Link
            to="tel:+911234567890"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-3 text-white transition hover:bg-white/20"
          >
            Reach out ↗
          </Link>

        </div>
      </footer>
    </div>
  );
}

function HomePage() {
  const [activeVideo, setActiveVideo] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonialItems = useMemo(() => testimonials, []);
  const activeVideoSlide = videoSlides[activeVideo];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonialItems.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [testimonialItems.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveVideo((prev) => (prev + 1) % videoSlides.length);
    }, 8000);

    return () => clearInterval(interval);
  }, []);

  const handleVideoShift = (direction: "prev" | "next") => {
    setActiveVideo((prev) => {
      if (direction === "prev") {
        return (prev - 1 + videoSlides.length) % videoSlides.length;
      }
      return (prev + 1) % videoSlides.length;
    });
  };


  return (
    <>
      <section id="hero" className="relative overflow-hidden pb-20 pt-16 lg:pt-24">
        <img
          src="/assets/figma/gradient-pill-stack.png"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -right-8 top-8 hidden h-[420px] w-[420px] opacity-80 lg:block"
        />

        <div className="mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-[1.25fr_0.75fr] lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative z-10 space-y-6"
          >
            <div className="space-y-6 rounded-3xl border border-white/20 bg-[#1E1E1E]/90 p-6 text-white shadow-card">
              <div className="flex items-center justify-between">
                <div className="flex gap-3" aria-label="Hero video controls">
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-white/10 transition hover:bg-white/20"
                    onClick={() => handleVideoShift("prev")}
                    aria-label="Previous video"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/40 bg-white/10 transition hover:bg-white/20"
                    onClick={() => handleVideoShift("next")}
                    aria-label="Next video"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="aspect-video w-full overflow-hidden rounded-2xl border border-white/20 bg-black">
                {activeVideoSlide.previewType === "video" ? (
                  (() => {
                    const media = resolveVideoMedia(activeVideoSlide);
                    if (!media) {
                      return null;
                    }

                    if (media.kind === "youtube" || media.kind === "iframe") {
                      return (
                        <iframe
                          src={media.src}
                          title={`${activeVideoSlide.title} video ${activeVideo + 1}`}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="h-full w-full"
                        />
                      );
                    }

                    return (
                      <video
                        src={media.src}
                        poster={activeVideoSlide.posterSrc}
                        controls
                        playsInline
                        className="h-full w-full object-cover"
                      />
                    );
                  })()
                ) : activeVideoSlide.previewType === "image" ? (
                  <img
                    src={activeVideoSlide.previewSrc}
                    alt={`${activeVideoSlide.title} preview ${activeVideo + 1}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div
                    className="h-full w-full"
                    style={{ backgroundColor: activeVideoSlide.previewColor || "#1E1E1E" }}
                  />
                )}
              </div>
            </div>

            <div className="space-y-6">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/60 px-4 py-2 text-xl font-semibold text-brand-muted shadow-card">
                About Us
              </span>
              <h1
                className="text-4xl font-semibold leading-tight text-brand-ink sm:text-5xl"
                style={{
                  fontSize: "20px"
                }}>
                Started back in 2016, Team Advaitha Acting & Direction Institute began its journey in Vijayanagar and later expanded to Indiranagar and Hebbal.
              </h1>
              <div className="space-y-4 text-sm text-brand-muted">
                <p>
                  Inspired by the legendary actor and director <strong>Shankar Nag</strong>, our founder <strong>Gangadhar Nagathihalli</strong> took a leap of faith—using whatever resources he had—to create a space where young talent could learn the art of acting and direction through real, hands-on experience.
                </p>
                <p>
                  Since then, we’ve grown into a trusted name in the industry, helping launch many familiar faces you've seen in movies, TV serials, YouTube short films, and web series.
                </p>
                <p>At <strong>Team Advaitha</strong>, we’re not just teaching—we’re building confidence, creativity, and careers.</p>
                <p>
                  To keep up with today’s fast-moving media world, we’ve recently added a fully equipped <strong>Dubbing</strong> and <strong>Post-Production Studio</strong>, where students can work with <strong>Netflix</strong>-approved cameras and professional gear. Our experienced mentors guide them through every step—whether it’s making a short film, learning how to face the camera, or editing like a pro.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <Link
                  to="https://www.youtube.com/@teamadvaitha" target="_blank"
                  className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-accent/90"
                >
                  Know More
                </Link>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="relative z-10"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <div className="space-y-3 rounded-3xl border border-white/30 bg-white/70 p-6 shadow-card backdrop-blur">
  {heroHighlights.map((item) => (
    <div
      key={item.href}
      className="flex items-center justify-between rounded-2xl border border-white/40 bg-white/70 px-4 py-3 text-sm font-semibold text-brand-ink shadow-inner"
    >
      <span>{item.label}</span>
      <a
        href={item.href}
        className="text-xs uppercase tracking-[0.2em] text-brand-muted"
      >
        Explore
      </a>
    </div>
  ))}
</div>

          </motion.div>
        </div>
      </section>
      <motion.section
        id="courses"
        className="mx-auto max-w-6xl px-6 pb-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h2 className="mt-2 text-3xl font-semibold text-brand-ink sm:text-4xl">
              Our Courses
            </h2>
          </div>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-3">
          {courses.map((course) => (
            <article
              key={course.title}
              className="group rounded-3xl border border-white/50 bg-white/80 p-8 shadow-card backdrop-blur transition hover:-translate-y-2 hover:shadow-glow"
            >
              <div className="space-y-4">
                <h3 className="text-2xl font-semibold text-brand-ink">{course.title}</h3>
                <p className="text-sm font-semibold uppercase tracking-[0.2em] text-brand-muted">{course.duration}</p>
                <p className="text-sm text-brand-muted">{course.description}</p>
              </div>
              <Link
                to={course.slug ? `/${course.slug}` : "#"}
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-ink/10 bg-white/80 px-5 py-2 text-sm font-semibold text-brand-ink transition hover:border-brand-ink/20 hover:bg-white"
              >
                Details
              </Link>
            </article>
          ))}
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {masterclasses.map((item) => (
            <article
              key={item.title}
              className="flex h-full flex-col justify-between rounded-3xl border border-white/50 bg-white/80 p-6 shadow-card transition hover:-translate-y-2 hover:shadow-glow"
            >
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-brand-ink">{item.title}</h3>
                <p className="text-sm text-brand-muted">{item.body}</p>
              </div>
              <Link
                to={item.slug ? `/${item.slug}` : "#"}
                className="mt-6 inline-flex w-max items-center gap-2 rounded-full border border-brand-ink/10 px-4 py-2 text-sm font-semibold text-brand-ink transition hover:border-brand-ink/20"
              >
                {item.cta}
              </Link>
            </article>
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href="tel:+9035979590"
            className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-accent/90"
          >
            Contact Now
          </a>
        </div>
      </motion.section>
      <motion.section
        id="studio"
        className="relative overflow-hidden bg-surface py-24 text-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        
        
        <div className="relative mx-auto max-w-6xl px-6 lg:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
              <h2 className="mt-2 text-3xl font-semibold text-white sm:text-4xl">
                Our Studio & Services
              </h2>
            </div>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {studioServices.map((service) => (
              <div
                key={service.title}
                className="flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-white/5 p-6 shadow-card transition hover:border-white/30 hover:bg-white/10"
              >
                <div className="space-y-3">
                  <h3 className="text-xl font-semibold text-white">{service.title}</h3>
                  <ul className="space-y-2 text-sm text-white/70">
                    {service.body.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
                <Link
                  to={`/${service.slug}`}
                  className="mt-6 inline-flex w-max items-center gap-2 rounded-full border border-white/10 bg-[#ec2127] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#d51c22]"

                >
                  Details
                </Link>
              </div>
            ))}
          </div>
        </div>
      </motion.section>
      <section id="portfolio" className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
        >
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h2 className="mt-2 text-3xl font-semibold text-brand-ink sm:text-4xl">Our Works</h2>
          </div>
        </motion.div>

        <div className="relative mt-12">
<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
  {portfolioItems.map((item, index) => (
    <div key={index} className="rounded shadow-lg overflow-hidden">
      <div className="w-full aspect-video">
        <iframe
          src={item.videoUrl}
          title={item.title}
          className="w-full h-full"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <div className="p-3 bg-white text-center text-sm font-semibold text-brand-ink">
        {item.title}
      </div>
    </div>
  ))}
</div>


        </div>
      </section>
      <section className="bg-white/80 py-24" id="testimonials">
        <motion.div
          className="mx-auto max-w-6xl px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">testimonials</p>
              <h2 className="mt-2 text-3xl font-semibold text-brand-ink sm:text-4xl">What they are saying about us</h2>
            </div>
            <div className="flex gap-3">
            </div>
          </div>

          <div className="relative mt-12">
            <div className="grid gap-6 md:grid-cols-[1.1fr_0.9fr]">
              {testimonialItems.map((testimonial, index) => {
                const isActive = index === activeTestimonial;
                return (
                  <motion.article
                    key={testimonial.name}
                    className={`rounded-3xl border border-brand-ink/5 bg-white p-8 shadow-card transition duration-500 ${
                      isActive ? "md:scale-[1.02]" : "md:opacity-80"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: isActive ? 1 : 0.6, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  >
                    <div className="flex gap-1 text-[#F1BE14]" aria-hidden>
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <span key={starIndex}>★</span>
                      ))}
                    </div>
                    <p className="mt-5 whitespace-pre-line text-base text-brand-ink">“{testimonial.text}”</p>
                    <div className="mt-8 text-sm font-semibold text-brand-ink">
                      {testimonial.name}
                      <p className="text-xs font-medium uppercase tracking-[0.3em] text-brand-muted">{testimonial.role}</p>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          </div>
        </motion.div>
      </section>
      <motion.section
        id="team"
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">our hardworking</p>
            <h2 className="mt-2 text-3xl font-semibold text-brand-ink sm:text-4xl">Team members</h2>
          </div>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
  {teamMembers.map((member) => (
    <div
      key={member.name}
      className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card backdrop-blur transition hover:-translate-y-1.5 hover:shadow-glow"
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-semibold text-brand-ink">{member.name}</h3>
          <p className="mt-2 text-xs uppercase tracking-[0.3em] text-brand-muted">
            {member.role}
          </p>
        </div>

        {/* Profile Photo (Slightly Bigger + Hover Scale) */}
        <img
          src={member.photo}
          className="h-17 w-17 rounded-full object-cover border border-accent/20 transition-transform hover:scale-110"
        />
      </div>

      {/* Profile Button */}
      <button
        onClick={() => window.open(member.profileLink, "_blank")}
        className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
      >
        Profile ↗
      </button>
    </div>
  ))}
</div>
      </motion.section>
      
<section className="mx-auto max-w-6xl px-6 py-20 lg:px-8">
      <h2 className="text-3xl font-bold text-brand-ink sm:text-4xl text-center">
        Our Alumni
      </h2>

      <div className="mt-10 overflow-hidden">
        <motion.div
          className="flex gap-6"
          animate={{ x: ["0%", "-100%"] }}
          transition={{
            repeat: Infinity,
            duration: 18,
            ease: "linear",
          }}
        >
          {[...alumni, ...alumni].map((item, index) => (
            <a
              key={index}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 w-[307px] h-[218px] rounded-2xl overflow-hidden border border-brand-ink/10 shadow-md"
            >
              <img
                src={item.img}
                alt="Our Alumni"
                className="w-full h-full object-cover"
              />
            </a>
          ))}
        </motion.div>
      </div>
    </section>

      <motion.section
        id="contact"
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="rounded-4xl overflow-hidden border border-brand-ink/5 bg-white/90 shadow-card backdrop-blur">
          <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
            <div className="space-y-10 border-b border-brand-ink/5 px-8 py-10 lg:border-b-0 lg:border-r">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-accent">Let's Talk</p>
                <h2 className="mt-3 text-3xl font-semibold text-brand-ink sm:text-4xl">Contact Us</h2>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                {contactOptions.map((option) => (
                  <a
                    key={option.label}
                    href={option.href}
                    className="flex items-center gap-3 rounded-2xl border border-brand-ink/5 bg-white px-4 py-3 text-sm font-semibold text-brand-ink shadow-card transition hover:-translate-y-1 hover:shadow-glow"
                  >
                    <option.icon className="h-5 w-5 text-accent" />
                    {option.label}
                  </a>
                ))}
              </div>

              <div className="rounded-3xl bg-brand-background/70 p-6 shadow-inner">
                <h3 className="text-lg font-semibold text-brand-ink">Request a callback to get personalized guidance</h3>
                <p className="mt-2 text-sm text-brand-muted">
                  Mob: 9035973590
                </p>
                <a
                  href="tel:+919035979590"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-accent/90"
                >
                  Request a Callback
                </a>
              </div>
            </div>

            <div className="space-y-6 px-8 py-10">
  <h3 className="text-lg font-semibold text-brand-ink">Branches</h3>
  <div className="space-y-5">
    {branches.map((branch) => (
      <div
        key={branch.name}
        className="relative rounded-2xl border border-brand-ink/5 bg-white/80 p-5 shadow-inner"
      >
        {/* Branch name + Get Directions on top */}
        <div className="flex items-start justify-between">
          <p className="text-sm font-semibold uppercase tracking-wide text-accent/90">
            {branch.name}
          </p>
          <button
            onClick={() => window.open(branch.mapLink, "_blank")}
            className="text-xs font-medium text-accent/80 transition hover:text-accent"
          >
           Google Maps  →
          </button>
        </div>

        {/* Address */}
        <p className="mt-2 whitespace-pre-line text-sm text-brand-muted">
          {branch.address}
        </p>
      </div>
    ))}
  </div>
</div>
          </div>
        </div>
      </motion.section>
    </>
  );
}

function DetailPage() {
  const params = useParams<{ slug: DetailSlug }>();
  const slug = params.slug;

  if (!slug || !(slug in slugRoutes)) {
    return <NotFound />;
  }

  const typedSlug = slug as DetailSlug;

  if (typedSlug === "acting-courses") {
    return <ActingDetail />;
  }

  if (typedSlug === "acting-courses-for-kids") {
    return <KidsActingDetail />;
  }

  if (typedSlug === "direction-courses") {
    return <DirectionDetail />;
  }

  if (typedSlug === "music-classes") {
    return <MusicDetail />;
  }

if (typedSlug === "dubbing-and-song-recording") {
  return <DubbingAndRecordingDetail />;
}

if (typedSlug === "editing-and-post-production") {
  return <EditingAndPostProduction />;
}

if (typedSlug === "music-production") { 
  return <MusicProduction />; 
}

if (typedSlug === "adshoot-and-photoshoot") {
  return <AdShootDetail />;
}

if (typedSlug === "audio-company") {
  return <AudioCompany />;
}


  if (typedSlug === "filmaking-masterclass") {
    return <MasterclassDetail />;
  }


  if (typedSlug === "camera-and-light-rentals") {
    return <CameraDetail />;
  }

  const data = slugRoutes[typedSlug];
  return <GenericDetail slug={typedSlug} data={data} />;
}

function GenericDetail({ slug, data }: { slug: DetailSlug; data: SubPageContent }) {
  const isDubbingDetail = slug === "dubbing-and-song-recording";

  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div
          className={`flex flex-col gap-6 ${
            isDubbingDetail ? "lg:flex-row-reverse lg:items-center lg:gap-12" : "sm:flex-row sm:items-center sm:justify-between"
          }`}
        >
          <div className={`space-y-4 ${isDubbingDetail ? "lg:max-w-xl" : "sm:max-w-xl"}`}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
            >
              ← Back to home
            </Link>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">{data.title}</h1>
            <p className="text-sm text-brand-muted">{data.heroSummary}</p>
            <div className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white">
              {data.duration}
            </div>
          </div>

          {isDubbingDetail && (
            <div className="overflow-hidden rounded-3xl border border-brand-ink/10 bg-white/80 shadow-card lg:w-[420px]">
              <img
                src="/assets/figma/dubbing-song-hero.jpg"
                alt="Sound engineer recording vocals inside the Team Advaitha studio"
                className="h-full w-full object-cover"
              />
            </div>
          )}
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-2">
          {data.sections.map((section) => (
            <article
              key={`${slug}-${section.heading}`}
              className="rounded-3xl border border-brand-ink/10 bg-white/90 p-6 shadow-card"
            >
              <h2 className="text-xl font-semibold text-brand-ink">{section.heading}</h2>
              {section.body && (
                <div className="mt-4 space-y-3 text-sm text-brand-muted">
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              )}
              {section.bullets && (
                <ul className="mt-4 space-y-2 text-sm text-brand-muted">
                  {section.bullets.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-1 block h-2 w-2 flex-shrink-0 rounded-full bg-accent" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </motion.section>
      <DetailCallToAction
        title={`Need more details on ${data.title}?`}
        body="Connect with our admissions team for schedules, batch availability, and fee structure."
      />
    </>
  );
}

function DetailCallToAction({
  title = "Ready to get started?",
  body = "Talk with our team for batch schedules, fee structure, and next steps.",
}: {
  title?: string;
  body?: string;
}) {
  return (
    <motion.section
      className="mx-auto max-w-6xl px-6 pb-24 lg:px-8"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeUp}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <div className="flex flex-col gap-4 rounded-3xl border border-brand-ink/5 bg-white/90 p-8 text-center shadow-card lg:flex-row lg:items-center lg:justify-between lg:text-left">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-brand-ink">{title}</h2>
          <p className="text-sm text-brand-muted">{body}</p>
        </div>
        <div className="flex flex-wrap justify-center gap-3 lg:justify-end">
          <Link
            to={{ pathname: "/", hash: "#contact" }}
            className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-accent/90"
          >
            Talk to us
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full border border-brand-ink/10 px-5 py-2 text-sm font-semibold text-brand-ink transition hover:border-brand-ink/20 hover:bg-white"
          >
            Explore homepage ↗
          </Link>
        </div>
      </div>
    </motion.section>
  );
}

function ActingDetail() {
  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="flex items-center justify-between gap-6">
          <div className="space-y-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
            >
              ← Back to home
            </Link>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h2
              className="text-4xl font-semibold text-brand-ink"
              style={{
                fontSize: "32px"
              }}>
              Acting Course
            </h2>
            <div className="inline-flex items-center gap-3 rounded-full bg-brand-ink px-5 py-2 text-sm font-semibold text-white">
              3 Months & 6 Months
            </div>
          </div>
          <div className="hidden lg:block">
            <img
              src="/assets/figma/5_Acting Course_1.jpg"
              alt="Acting class still"
              className="w-full max-w-sm justify-self-center overflow-hidden rounded-3xl border border-brand-ink/10 shadow-card shadow-md h-54 w-54"
            />
          </div>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-6">
            <div className="grid gap-4 rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card sm:grid-cols-2">
              <div>
                <h3 className="text-lg font-semibold text-brand-ink">Locations</h3>
                <p className="mt-2 whitespace-pre-line text-sm text-brand-muted">Bengaluru
Indiranagar · Hebbal · Vijayanagar</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-brand-ink">Student Support</h3>
                <p className="mt-2 text-sm text-brand-muted">Portfolio shoots, audition prep, live set exposure.</p>
              </div>
              <div className="sm:col-span-2 rounded-2xl border border-brand-ink/5 bg-white/80 p-4 text-sm text-brand-muted shadow-inner">
                Mentorship continues even after graduation with casting alerts and production updates.
              </div>
            </div>

            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-xl font-semibold text-brand-ink">Syllabus</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {actingCourseLessons.map((lesson) => (
                  <div key={lesson.detail} className="rounded-2xl border border-brand-ink/5 bg-white/80 p-4 shadow-inner">
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-muted">{lesson.title}</p>
                    <p className="mt-2 text-sm text-brand-ink">{lesson.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-xl font-semibold text-brand-ink">Pricing & Inclusions</h3>
              <p className="mt-3 text-sm text-brand-muted">
                Weekday batches run Wednesday to Sunday (11:00 AM – 2:00 PM); weekend batches operate on Saturday & Sunday.
              </p>
              <div className="mt-12">
  <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-4 shadow-card">
    <a target="_blank" rel="noopener noreferrer">
      <img
        src="/assets/figma/5_Acting Course_2.png"
        alt="Acting Course Banner"
      />
      <img
        src="/assets/figma/5_Acting Course_3.png"
        alt="Acting Course Banner"
      />
    </a>
  </div>
</div>

            </div>
          </div>
        </div>
      </motion.section>
      <motion.section
        className="mx-auto max-w-6xl px-6 pb-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
            <h3 className="text-xl font-semibold text-brand-ink">Student Short Films</h3>
            <p className="mt-3 text-sm text-brand-muted">
              Discover how our batches collaborate with mentors to produce short films as their capstone projects.
            </p>
            <div className="mt-6 aspect-video overflow-hidden rounded-2xl border border-brand-ink/10">
              <iframe
                width="560"
                height="315"
                src="https://www.youtube.com/embed/jYeJzFp1Z8w?si=hfj5evk6PWKXQBif"
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                className="h-full w-full"
              />
            </div>
          </div>

          <DetailCallToAction
            title="Ready to join our Acting Courses?"
            body="Speak with Team Advaitha to understand batches, auditions, and enrolment support."
          />
        </div>
      </motion.section>
    </>
  );
}

function KidsActingDetail() {
  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="flex items-center justify-between gap-6">
          <div className="space-y-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
            >
              ← Back to home
            </Link>
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h2
              className="text-4xl font-semibold text-brand-ink"
              style={{
                fontSize: "32px"
              }}>Acting Course for kids</h2>
            <div className="inline-flex items-center gap-3 rounded-full bg-brand-ink px-5 py-2 text-sm font-semibold text-white">
              6 Months & 1 Year
            </div>
          </div>
          <div className="hidden lg:block">
            <img
              src="/assets/figma/6_Kids Acting_1.jpg"
              alt="Kids acting class"
              className="w-full max-w-sm justify-self-center overflow-hidden rounded-3xl border border-brand-ink/10 shadow-card shadow-md h-54 w-54"
            />
          </div>
        </div>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-6">
            <div className="grid gap-4 rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card sm:grid-cols-2">
              {kidCourseLocations.map((item) => (
                <div key={item.label} className="rounded-2xl border border-brand-ink/5 bg-white/80 p-4 shadow-inner">
                  <p className="text-xs uppercase tracking-[0.3em] text-brand-muted">{item.label}</p>
                  <p className="mt-2 text-sm text-brand-ink">{item.description}</p>
                </div>
              ))}
            </div>

            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-xl font-semibold text-brand-ink">Syllabus</h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {kidCourseLessons.map((lesson) => (
                  <div key={lesson.detail} className="rounded-2xl border border-brand-ink/5 bg-white/80 p-4 shadow-inner">
                    <p className="text-xs uppercase tracking-[0.3em] text-brand-muted">{lesson.title}</p>
                    <p className="mt-2 text-sm text-brand-ink">{lesson.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-xl font-semibold text-brand-ink">Pricing & Inclusions</h3>
              <p className="mt-3 text-sm text-brand-muted">Only on Sundays · 11:00 AM – 2:00 PM</p>
              <div className="mt-12">
  <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-4 shadow-card">
    <a target="_blank" >
      <img
        src="/assets/figma/6_Kids Acting_2.png"
        alt="Acting Course for kids"
        className="w-full rounded-2xl object-cover transition"
      />
    </a>
  </div>
</div>

            </div>

            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-xl font-semibold text-brand-ink">Student Short Films</h3>
              <p className="mt-3 text-sm text-brand-muted">
                Watch our young actors shine in their capstone short film projects.
              </p>
              <div className="mt-6 aspect-video overflow-hidden rounded-2xl border border-brand-ink/10">
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/OORqQvNj8JA?si=FoR5RgabNp9ufobh"
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      <DetailCallToAction
        title="Ready to enrol your child?"
        body="Connect with Team Advaitha to choose the right batch and get guidance on auditions."
      />
    </>
  );
}

function DirectionDetail() {
  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8"
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        style={{ opacity: 0, transform: "translateY(20px)" }}
      >
        <div className="space-y-10 lg:space-y-14">
          <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="space-y-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
              >
                ← Back to home
              </Link>
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
              <h2 className="text-4xl font-semibold text-brand-ink">
                Direction Courses
              </h2>
              <div className="grid gap-4 rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card md:grid-cols-2">
                <div>
                  <h3 className="text-lg font-semibold text-brand-ink">3 Months</h3>
                  <p className="mt-2 text-sm text-brand-muted">Ideal for beginners who want a strong foundation in the art and craft of direction.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-brand-ink">6 Months</h3>
                  <p className="mt-2 text-sm text-brand-muted">A comprehensive program for those ready to take their filmmaking skills to a professional level.</p>
                </div>
                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold text-brand-ink">Location</h3>
                  <p className="mt-2 whitespace-pre-line text-sm text-brand-muted">Bangalore
Vijayanagar & Indiranagar</p>
                </div>
              </div>
            </div>

            {/* HERO IMAGE */}
            <div className="w-full max-w-sm justify-self-center overflow-hidden rounded-3xl border border-brand-ink/10 shadow-card shadow-md">
              <img
                src="/assets/figma/direction-hero.jpg"
                alt="Direction course behind the scenes"
                className="h-full w-full object-cover"
              />
            </div>
          </div>

          {/* ✅  SYLLABUS */}
          <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
            <h3 className="text-xl font-semibold text-brand-ink">Syllabus</h3>
            <div className="mt-4 space-y-3">
              {directionCourseLessons.map((lesson) => (
                <div key={lesson.title} className="rounded-2xl border border-brand-ink/5 bg-white/80 p-4 shadow-inner">
                  <p className="text-sm font-semibold text-brand-ink">{lesson.title}</p>
                  <p className="mt-2 text-sm text-brand-muted">{lesson.detail}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ✅ RESPONSIVE PRICING IMAGES */}
          <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card space-y-6">
            <h3 className="text-xl font-semibold text-brand-ink">Pricing & Inclusions</h3>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-2 shadow-card w-full cursor-pointer">
                <img
                  src="/assets/figma/direction-card-1.jpg"
                  alt="Direction Course Banner 1"
                  className="w-full h-[350px] sm:h-[420px] md:h-[480px] rounded-2xl object-cover"
                />
              </div>
              <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-2 shadow-card w-full cursor-pointer">
                <img
                  src="/assets/figma/7_Direction_3.png"
                  alt="Direction Course Banner 2"
                  className="w-full h-[350px] sm:h-[420px] md:h-[480px] rounded-2xl object-cover"
                />
              </div>
            </div>
          </div>

          {/* ✅ OUTCOMES */}
          <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
            <h3 className="text-lg font-semibold text-brand-ink">{directionCourseOutcomes[0]}</h3>
            <p className="mt-2 text-sm font-semibold text-brand-muted">{directionCourseOutcomes[1]}</p>
            <ul className="mt-4 space-y-2 text-sm text-brand-ink">
              {directionCourseOutcomes.slice(2).map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <span className="mt-1 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-accent" aria-hidden />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ✅ FACULTY */}
          <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
            <h3 className="text-lg font-semibold text-brand-ink">Our faculty includes</h3>
            <ul className="mt-3 space-y-2 text-sm text-brand-muted">
              {directionCourseFaculty.map((faculty) => (
                <li key={faculty}>{faculty}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* ✅ STUDENT VIDEOS */}
        <div className="mt-10 space-y-6">
          <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
            <h3 className="text-xl font-semibold text-brand-ink">Student Short Films</h3>
            <p className="mt-2 text-sm text-brand-muted">Explore the short films directed and produced by our students.</p>
            <div className="mt-6 grid gap-6 lg:grid-cols-3">
              {[
                "https://www.youtube.com/embed/KdWp6gEoIw0?si=kS8IgpDGpsBY5Cxo",
                "https://www.youtube.com/embed/fXGHRbAA1e4?si=0wGsQcXu67kPQOoW",
                "https://www.youtube.com/embed/kowYo7y2yB0?si=kilv2XThmU13WYBE"
              ].map((src) => (
                <div key={src} className="aspect-video overflow-hidden rounded-2xl border border-brand-ink/10">
                  <iframe
                    width="560"
                    height="315"
                    src={src}
                    title="Direction course student short film"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="h-full w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <DetailCallToAction
        title="Start your Direction journey"
        body="Reach out for mentoring schedules, production opportunities, and fee details."
      />
    </>
  );
}

function MusicDetail() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;
  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>
        </div>
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-6">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card space-y-4">
              <h2 className="text-4xl font-semibold text-brand-ink">
                Music Classes
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <h3 className="text-lg font-semibold text-brand-ink">Courses</h3>
                  <p className="mt-2 text-sm text-brand-muted">Keyboard, Guitar, Hindustani & Carnatic Vocal</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-brand-ink">Location</h3>
                  <p className="mt-2 text-sm text-brand-muted">Vijayanagar</p>
                </div>
              </div>
              <Link
                to={contactLink}
                className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
              >
                Contact for batches ↗
              </Link>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-xl font-semibold text-brand-ink">Syllabus</h3>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {musicClassSyllabus.map((level) => (
                  <div key={level.title} className="rounded-2xl border border-brand-ink/5 bg-white/80 p-4 shadow-inner">
                    <h4 className="text-sm font-semibold text-brand-ink">{level.title}</h4>
                    <ul className="mt-3 space-y-2 text-sm text-brand-muted">
                      {level.bullets.map((point) => (
                        <li key={point}>{point}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      <motion.section
        className="mx-auto max-w-6xl px-6 pb-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">Music Instructor Works</h2>
            <p className="text-sm text-brand-muted">
              Watch select performances and session videos from our instructors that showcase the skill and passion they bring to every class.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-3">
            {[
              "https://www.youtube.com/embed/jYeJzFp1Z8w?si=yJK2Kj5DCsnt8acu",
              "https://www.youtube.com/embed/Nfg_3CPzALQ?si=gySuFfXMgzQI_4a3",
              "https://www.youtube.com/embed/LnOm6V2WlhQ?si=Rnx-gWxQKfqRGaoj",
            ].map((src, index) => (
              <div
                key={src}
                className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card"
              >
                <div className="aspect-video">
                  <iframe
                    src={src}
                    title={`Music instructor performance ${index + 1}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="h-full w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>
      <DetailCallToAction
        title="Book your music session"
        body="Chat with our coordinators to pick instruments, schedules, and personalized learning plans."
      />
    </>
  );
}


function AdShootDetail() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;

  const videoUrls = [
    "https://www.youtube.com/embed/0Dqjn2hIh4g?si=x-o_1zdhGb1PDL76",
    "https://www.youtube.com/embed/W-Ufg39rmJE?si=8Z52J3X1Po1wfBWr",
    "https://www.youtube.com/embed/Z1lxWNnc_lo?si=RoAFKFRfnK9QeYFY",
    "https://www.youtube.com/embed/UkRn_oaV-Oc?si=qns20R-IDhXnAQq1",
    "https://www.youtube.com/embed/L_4s9oqmMRk?si=r6UsLQnnpuhTyCz6",
    "https://www.youtube.com/embed/cnC2LPPoJ5g?si=z6crfEJvf9zRgRgf",
  ];

  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Intro */}
        <div className="mb-16 space-y-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>

          <p className="text-sm font-semibold uppercase tracking-wide text-accent">
            Explore
          </p>

          <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">
            Ad Flims
          </h1>

          <p className="text-base text-brand-muted max-w-xl mt-6">
            Concept-to-camera production unit for brand ads, product shoots, and talent portfolios.
          </p>

          <Link
            to="https://drive.google.com/file/d/1Q8V5EBfwbRY5w51JTzDA1XmWowIG4GVN/view?usp=sharing"
            className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
          >
            Rate Card ↗
          </Link>
        </div>

        {/* Offerings + Ideal Clients */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Offerings</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Creative direction and scripting</li>
              <li>Studio and outdoor production teams</li>
              <li>Retouching, edits, and final delivery</li>
            </ul>
          </div>

          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Ideal Clients</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Lifestyle, FMCG, and startup brands</li>
              <li>Emerging artists and influencers</li>
            </ul>
          </div>
        </div>
      </motion.section>
<section className="mx-auto max-w-6xl px-6 pb-24 lg:px-8 space-y-6">
  <div className="flex items-center justify-between space-x-6">
    <div className="flex-1">
      <p className="text-sm font-semibold uppercase tracking-wide text-accent">
        Showcase
      </p>
      <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">
        Our Flims
      </h2>
      <p className="text-sm text-brand-muted"></p>
    </div>
  </div>

  {/* Videos grid: 3 rows x 2 columns */}
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
    {videoUrls.slice(0, 6).map((src, index) => (
      <div
        key={src}
        className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card"
      >
        <div className="aspect-video">
          <iframe
            src={src}
            title={`Music Production video ${index + 1}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      </div>
    ))}
  </div>
</section>
      <DetailCallToAction
        title="Need an ad or product shoot?"
        body="Share your project and we’ll craft a stunning visual campaign."
      />
    </>
  );
}

function AudioCompany(){
  const contactLink = { pathname: "/", hash: "#contact" } as const;
  const videoUrls = [
    "https://www.youtube.com/embed/jK58g55Z_TM?si=YXcsemV6H9iz2Urj",
    "https://www.youtube.com/embed/MmnxvoXS6IE?si=UUAhyvzZUi5tp0CO",
    "https://www.youtube.com/embed/z2ZwRzOMK3M?si=tacAKCf0HmTTPPR_",
    "https://www.youtube.com/embed/XVPsWmGlbcE?si=c_Zb3OTv7h04Awvh",
    "https://www.youtube.com/embed/Nu4rojktWCo?si=GGoDfdkMuc6eD_9P",
    "https://www.youtube.com/embed/t5G9DWn4-5Q?si=sGIWURaxLksu9bfr",
  ];

  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        {/* Intro */}
        <div className="mb-16 space-y-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>

          <p className="text-sm font-semibold uppercase tracking-wide text-accent">
            Explore
          </p>

          <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">
            Audio Company
          </h1>

          <p className="text-base text-brand-muted max-w-xl mt-6">
            We distribute music to major streaming platforms and manage copyrights, produce, & Distribute content for Independent artists and Music Albums globally.
          </p>

          <Link
            to={contactLink}
            className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
          >
            Annual Partnership ↗
          </Link>
        </div>

        {/* Offerings + Ideal Clients */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Highlights</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>We are an independent digital music distribution company.</li>
              <li>We endeavor to help our local and regional artists and generate revenue for them.</li>
              <li>We produce music Videos.</li>
              <li>We have over 20+ stores and distribute songs to all the major players in the music industry.</li>
            </ul>
          </div>

          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Distribution Platforms</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Spotify</li>
              <li>Apple Music</li>
              <li>Amazon Music</li>
              <li>YouTube Music</li>
              <li>Wynk</li>
              <li>JioSaavn</li>
              <li>Gaana</li>
              <li>Deezer</li>
              <li>SoundCloud</li>
            </ul>
          </div>
        </div>
      </motion.section>
      <section className="mx-auto max-w-6xl px-6 pb-24 lg:px-8 space-y-6">
        <div className="flex items-center justify-between space-x-6">
          <div className="flex-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">
              Showcase
            </p>
            <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">
             Our Published Catalog
            </h2>
            <p className="text-sm text-brand-muted">
              
            </p>
          </div>
        </div>

        {/* Videos grid: 2 rows x 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {videoUrls.map((src, index) => (
            <div
              key={src}
              className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card"
            >
              <div className="aspect-video">
                <iframe
                  src={src}
                  title={`Music Production video ${index + 1}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>
          ))}
        </div>
      </section>
<section className="mx-auto max-w-6xl px-6 pb-24 lg:px-8 space-y-6">
  <div className="flex items-center justify-between space-x-6">
    <div className="flex-1">
      <p className="text-sm font-semibold uppercase tracking-wide text-accent">
        Showcase
      </p>
      <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">
        Artists & Musicians we have collaborated with so far
      </h2>
      <p className="text-sm text-brand-muted"></p>
    </div>
  </div>

  {/* Image grid: 1 row x 4 columns with spacing */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-8 justify-items-center">
    {[
      {
        img: "/assets/figma/11_Audio Company_2.jpg",
        link: "https://example.com/artist1",
      },
      {
        img: "/assets/figma/11_Audio Company_3.jpg",
        link: "https://example.com/artist2",
      },
      {
        img: "/assets/figma/11_Audio Company_4.jpg",
        link: "https://example.com/artist3",
      },
      {
        img: "/assets/figma/11_Audio Company_5.jpg",
        link: "https://example.com/artist4",
      },
    ].map(({ img, link }, index) => (
      <a
        key={index}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card transition-transform hover:scale-105"
        style={{ width: "290px", height: "272px" }}
      >
        <img
          src={img}
          alt={`Showcase image ${index + 1}`}
          className="w-full h-full object-cover"
        />
      </a>
    ))}
  </div>
</section>
      <DetailCallToAction
        title="Need an ad or product shoot?"
        body="Share your project and we’ll craft a stunning visual campaign."
      />
    </>
  );
}


function DubbingAndRecordingDetail() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;

  return (
    <>
      <section className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        {/* 🧭 Top Section: Text Left, Image Right */}
        <div className="mb-16 grid gap-10 lg:grid-cols-2 lg:items-center">
          {/* 📝 Text on the Left */}
          <div className="space-y-6">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
            >
              ← Back to home
            </Link>

            <p className="text-sm font-semibold uppercase tracking-wide text-accent">
              Explore
            </p>

            <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">
              Dubbing & Song Recording
            </h1>

            <p className="text-base text-brand-muted max-w-xl">
              Professional Song Recording & Dubbing Services at affordable prices with a Sound Engineer with 4 years of experience.
            </p>

            <Link
              to="https://drive.google.com/file/d/1Q8V5EBfwbRY5w51JTzDA1XmWowIG4GVN/view?usp=sharing"
              className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
            >
              Rate Card ↗
            </Link>
          </div>

          {/* 🖼️ Image on the Right */}
          <div className="overflow-hidden rounded-3xl shadow-xl border border-brand-ink/10">
            <img
              src="\assets\figma\dubbing-song-hero.jpg" // 🔁 Update this path as needed
              alt="Dubbing Studio"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>

        {/* 📦 Bottom: Highlights and Tech Specs */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* 🎯 Highlights */}
          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Highlights</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>On-spot pitch correction for Song Recording.</li>
              <li>Emotional Correction for Dubbing.</li>
              <li>Affordable Prices for Independent Filmmakers.</li>
            </ul>
          </div>

          {/* 🛠️ Tech Specs */}
          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Tech Specs</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Audio-Technica AT2050 Multi-Pattern Condenser</li>
              <li>Microphone - Yamaha HS8 Studio Monitor</li>
              <li>Focusrite Scarlett 2i2 4th Gen audio interface</li>
              <li>Sennheiser Professional Audio HD 280 PRO</li>
              <li>Acoustic Soundproof Vocal Booth</li>
            </ul>
          </div>
        </div>
      </section>
      <DetailCallToAction
        title="Need more details on Dubbing & Song Recording?"
        body="Connect with our admissions team for schedules, batch availability, and fee structure."
      />
    </>
  );
}


function EditingAndPostProduction() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;

  const showcaseSections = [
    {
      title: "Movies / Webseries",
      subtitle: "Trailers, edits, and full-length narratives crafted for the screen.",
      videos: [
        "https://www.youtube.com/embed/zPa_TYSHjzg?si=DZgqFdYm43mlU0Q1",
        "https://www.youtube.com/embed/jK58g55Z_TM?si=OE_SOQy8QAgtIUhM",
        "https://www.youtube.com/embed/AdPkexa1f1U",
      ],
    },
    {
      title: "VFX",
      subtitle: "Visual effects breakdowns, compositing layers, and final renders.",
      videos: [
        "https://www.youtube.com/embed/Q7YT4xpzwjQ?si=RJQL-P3ZTUD4qI0o",
        "https://www.youtube.com/embed/GWNjFkeFags?si=ruDFdvRyHGxjfpW5",
        "https://www.youtube.com/embed/PJaGE797bHQ?si=QY0Uzy9HKctIKPkK",
      ],
    },
    {
      title: "3D Animation",
      subtitle: "Previs, 3D motion, character animation and more.",
      videos: [
        "https://www.youtube.com/embed/TBLPE45deDM?si=lE4vMxvLLutV0HWM",
        "https://www.youtube.com/embed/L_4s9oqmMRk?si=M50f6XrpZ0GEn7eX",
        "https://www.youtube.com/embed/d05kE0wuN0E?si=DhlWhOVnkW9HtkjR",
      ],
    },
    {
      title: "Title Animations",
      subtitle: "Opening title sequences and kinetic typography reels.",
      videos: [
        "https://www.youtube.com/embed/0-0nueBL4d0?si=3AOU1-c0fQzisYZD",
        "https://www.youtube.com/embed/ENZKBWaA14c?si=b3st4ch705wh4Y8J",
        "https://www.youtube.com/embed/JKEdCLMu_Yk?si=yV6yGqxBmk3LBHA6",
      ],
    },
    {
      title: "Photoshoot",
      subtitle: "Behind-the-scenes of concept shoots, portraits, and creative sets.",
      videos: [
        "https://www.youtube.com/embed/UkRn_oaV-Oc?si=Nz4dHShXXF57Mf0g",
        "https://www.youtube.com/embed/mbxepSb207E?si=1T8373AW226pqCdL",
      ],
    },
  ];

  return (
    <>
      {/* 🧭 Intro */}
      <section className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        <div className="mb-16 space-y-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>

          <p className="text-sm font-semibold uppercase tracking-wide text-accent">
            Explore
          </p>

          <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">
            Post-Production
          </h1>

          <p className="text-base text-brand-muted max-w-xl">
            One Stop Solution for Ad Films, Short Films, Webseries & Movies
          </p>

          <Link
            to="https://drive.google.com/file/d/1Q8V5EBfwbRY5w51JTzDA1XmWowIG4GVN/view?usp=sharing"
            className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
          >
     Rare Card ↗
          </Link>
        </div>

        {/* 🧾 Capabilities and Deliverables */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Services</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Editing</li>
              <li>Color Grading (Di)</li>
              <li>SFX & Foley</li>
              <li>Mixing</li>
              <li>Mastering (Stereo)</li>
              <li>Previz</li>
              <li>3D Animation</li>
              <li>VFX</li>
              <li>Title Animation</li>
              <li>3D Product Renders</li>
              <li>Photoshoots</li>
              <li>Product Shoot</li>
              <li>Corporate Events</li>
              <li>Pre-wedding Shoots</li>
            </ul>
          </div>

          
        </div>
      </section>

      {/* 🎬 Video Showcase Sections */}
      {showcaseSections.map((section) => (
        <section key={section.title} className="mx-auto max-w-6xl px-6 pb-24 lg:px-8">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-sm font-semibold uppercase tracking-wide text-accent">Showcase</p>
              <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">{section.title}</h2>
              <p className="text-sm text-brand-muted">{section.subtitle}</p>
            </div>

            <div
              className={`grid gap-8 ${
                section.videos.length === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"
              }`}
            >
              {section.videos.map((src, index) => (
                <div
                  key={`${section.title}-video-${index}`}
                  className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card"
                >
                  <div className="aspect-video">
                    <iframe
                      src={src}
                      title={`${section.title} video ${index + 1}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                      className="h-full w-full"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}
      <DetailCallToAction
        title="Need more details on Editing & Post Production?"
        body="Connect with our admissions team for schedules, batch availability, and fee structure."
      />
    </>
  );
}



function MusicProduction() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;

  // 6 video URLs (replace with actual links)
  const videoUrls = [
    "https://www.youtube.com/embed/jK58g55Z_TM?si=YXcsemV6H9iz2Urj",
    "https://www.youtube.com/embed/MmnxvoXS6IE?si=UUAhyvzZUi5tp0CO",
    "https://www.youtube.com/embed/z2ZwRzOMK3M?si=tacAKCf0HmTTPPR_",
    "https://www.youtube.com/embed/XVPsWmGlbcE?si=c_Zb3OTv7h04Awvh",
    "https://www.youtube.com/embed/Nu4rojktWCo?si=GGoDfdkMuc6eD_9P",
    "https://www.youtube.com/embed/t5G9DWn4-5Q?si=sGIWURaxLksu9bfr",
  ];

  return (
    <>
      <section className="mx-auto max-w-6xl px-6 py-24 lg:px-8">
        {/* Intro */}
        <div className="mb-16 space-y-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>

          <p className="text-sm font-semibold uppercase tracking-wide text-accent">
            Explore
          </p>

          <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">
            Music Production
          </h1>

          <p className="text-base text-brand-muted max-w-xl mt-6">
            Comprehensive training in music production, beat-making, mixing, and mastering.
          </p>

          <Link
            to={contactLink}
            className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
          >
             Rate Card ↗
          </Link>
        </div>

        {/* Capabilities and Deliverables */}
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Capabilities</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Pro Tools, Logic Pro, Ableton workflows</li>
              <li>Beat making, sound design, and audio engineering</li>
              <li>Mixing, mastering, and sound effects integration</li>
            </ul>
          </div>

          <div className="rounded-3xl border border-brand-ink/10 bg-white p-6 shadow-card space-y-4">
            <h3 className="text-lg font-semibold text-brand-ink">Deliverables</h3>
            <ul className="space-y-2 text-sm text-brand-muted list-disc list-inside">
              <li>Original beats and tracks</li>
              <li>Fully mixed and mastered audio projects</li>
              <li>Portfolio-ready production samples</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="mx-auto max-w-6xl px-6 pb-24 lg:px-8 space-y-6">
        <div className="flex items-center justify-between space-x-6">
          <div className="flex-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">
              Showcase
            </p>
            <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">
              Music Production Highlights
            </h2>
            <p className="text-sm text-brand-muted">
              Experience projects demonstrating beat-making, mixing, and mastering.
            </p>
          </div>
        </div>

        {/* Videos grid: 2 rows x 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {videoUrls.map((src, index) => (
            <div
              key={src}
              className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card"
            >
              <div className="aspect-video">
                <iframe
                  src={src}
                  title={`Music Production video ${index + 1}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            </div>
          ))}
        </div>
      </section>
      <DetailCallToAction
        title="Need more details on Music Production?"
        body="Connect with our admissions team for schedules, batch availability, and fee structure."
      />
    </>
  );
}



function MasterclassDetail() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;
  return (
    <>
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>
        </div>
        <div className="space-y-10">
          <div className="space-y-4">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <div className="flex flex-col-reverse gap-6 lg:flex-row lg:items-start lg:gap-8">
              <div className="space-y-4 rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card lg:flex-1">
                <h2 className="text-4xl font-semibold text-brand-ink">
                  Filmaking Masterclass
                </h2>
                <div className="grid gap-4 rounded-2xl bg-white/70 p-4 text-sm text-brand-muted md:grid-cols-3">
                  <span>3 Hours Daily</span>
                  <span>7 Intensive Days</span>
                  <span>Selected Direction Students</span>
                </div>
                <Link
                  to="https://www.behance.net/sunilfx"
                  className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
                >
      Portfolio ↗
                </Link>
              </div>
              <div className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/80 shadow-inner lg:w-[320px]">
                <img
                  src="/assets/figma/8_Filmamking-100.jpg"
                  alt="Filmmaking masterclass session"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="grid gap-6 rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card lg:grid-cols-2">
              <div>
                <h3 className="text-lg font-semibold text-brand-ink">Theory</h3>
                <ul className="mt-3 space-y-2 text-sm text-brand-muted">
                  {masterclassTheory.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-brand-ink">Practicals</h3>
                <ul className="mt-3 space-y-2 text-sm text-brand-muted">
                  {masterclassPracticals.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-lg font-semibold text-brand-ink">About the instructor</h3>
              <p className="mt-2 text-sm text-brand-muted">
                A professional Creative Director with 14 years of experience in Filmmaking, Post Production & 3D Animation. With sound business acumen & in-depth knowledge. Started 3 companies & led creative teams.
              </p>
            </div>
            <div className="rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card">
              <h3 className="text-lg font-semibold text-brand-ink">Theory sessions</h3>
              <p className="mt-2 text-sm text-brand-muted">
                Theory covers all aspects from idea to marketing so the copy matches exactly the Figma copy deck.
              </p>
            </div>
          </div>
        </div>
      </motion.section>
      <motion.section
        className="mx-auto max-w-6xl px-6 pb-24 lg:px-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeUp}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="space-y-6">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>
            <h2 className="text-3xl font-semibold text-brand-ink sm:text-4xl">Instructor Works</h2>
            <p className="text-sm text-brand-muted">
              Explore selected film projects and behind-the-scenes insights directed and produced by our masterclass instructors.
            </p>
          </div>
          <div className="grid gap-8 lg:grid-cols-2">
            {[
              "https://www.youtube.com/embed/0Dqjn2hIh4g?si=NFx4C0scdbgmDanR",
              "https://www.youtube.com/embed/TBLPE45deDM?si=NcwjMofqn_YWFDjC",
              "https://www.youtube.com/embed/cnC2LPPoJ5g?si=VqotPoNBKHL3RtbP",
              "https://www.youtube.com/embed/ecVSMSl3jBQ?si=m9AO6iZM0TRbCqTN",
            ].map((src, index) => (
              <div
                key={src}
                className="overflow-hidden rounded-3xl border border-brand-ink/5 bg-white/90 shadow-card"
              >
                <div className="aspect-video">
                  <iframe
                    src={src}
                    title={`Filmmaking instructor work ${index + 1}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                    className="h-full w-full"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.section>
      <DetailCallToAction
        title="Apply for the Filmmaking Masterclass"
        body="Message us to confirm eligibility, fees, and the next cohort dates."
      />
    </>
  );
}

function CameraDetail() {
  const contactLink = { pathname: "/", hash: "#contact" } as const;

  return (
    <>
      {/* ✅ Changed whileInView -> animate (stable on mobile refresh) */}
      <motion.section
        className="mx-auto max-w-6xl px-6 py-24 lg:px-8"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      >
        <div className="mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-accent transition hover:gap-3"
          >
            ← Back to home
          </Link>
        </div>

        <p className="text-sm font-semibold uppercase tracking-wide text-accent">Explore</p>

        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <h2 className="mt-2 text-3xl font-semibold text-brand-ink sm:text-4xl">
            Camera & Equipment Rentals
          </h2>
          <Link
            to={contactLink}
            className="inline-flex w-max items-center gap-2 rounded-full bg-brand-ink px-4 py-2 text-sm font-semibold text-white shadow-glow transition hover:bg-brand-ink/90"
          >
            Enquire about rentals ↗
          </Link>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {gearCategories.map((gear, index) => (
            <motion.div
              key={gear.title}
              className="space-y-4 rounded-3xl border border-brand-ink/5 bg-white/90 p-6 shadow-card"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
            >
              <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-brand-ink/10 bg-white/70">
                <img
                  src={gear.image}
                  alt={`${gear.title} equipment visual`}
                  className="h-full w-full object-cover"
                  loading={index < 3 ? "eager" : "lazy"}
                />
              </div>

              <div>
                <h3 className="text-lg font-semibold text-brand-ink">{gear.title}</h3>
                <p className="mt-2 text-sm text-brand-muted">{gear.subtitle}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <DetailCallToAction
        title="Need a production-ready rental package?"
        body="Share your shoot requirements and we’ll customise the camera and lighting bundle for you."
      />
    </>
  );
}


function NotFound() {
  return (
    <motion.section
      className="mx-auto max-w-3xl px-6 py-32 text-center"
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <h1 className="text-4xl font-semibold text-brand-ink sm:text-5xl">Page not found</h1>
      <p className="mt-4 text-sm text-brand-muted">
        The requested page could not be found. Please return to the homepage to keep exploring Team Advaitha.
      </p>
      <Link
        to="/"
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-white shadow-glow transition hover:bg-accent/90"
      >
        Back to home
      </Link>
    </motion.section>
  );
}

export default function App() {
  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path=":slug" element={<DetailPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
